<template>
  <!-- Search Tabs -->
  <div>
    <b-tabs
      v-model="activeTab"
      type="is-toggle"
      :animated="false"
    >
      <b-tab-item
        label="Search this page"
        :disabled="searchThisPageDisabled"
      >
        <b-field>
          <b-input
            v-model="searchQuery"
            placeholder="Search this page..."
            type="search"
            icon="search"
            icon-clickable
            aria-label="Search this page"
            @icon-click="searchPage"
            @keyup.native.enter="searchPage"
          />
          <i
            v-if="searchQuery" 
            class="fas fa-times"
            @click="clearPageSearch()"
          />
        </b-field>
      </b-tab-item>
      <b-tab-item label="Search all pages">
        <b-field>
          <b-input
            v-model="searchQuery"
            placeholder="Search all pages..."
            type="search"
            icon="search"
            icon-clickable
            aria-label="Search all pages"
            @icon-click="searchAllPages"
            @keyup.native.enter="searchAllPages"
          />
          <i
            v-if="searchQuery" 
            class="fas fa-times"
            @click="clearAllPageSearch()"
          />
        </b-field>
      </b-tab-item>
    </b-tabs>
    <div v-if="searchedText">
      <span
        v-if="searchCount === 0" 
        class="page-search-results"
      >
        No results found for <strong><em>"{{ searchedText }}"</em></strong> 
      </span>
      <span
        v-if="searchCount > 0"
        class="page-search-results"
      >
        Showing {{ searchCount }} results for <strong><em>"{{ searchedText }}"</em></strong>
      </span>
      <input
        type="submit"
        class="clear-button"
        value="Clear all"
        @click="clearAllPageSearch()"
      >
      <div 
        v-if="searchCount === 0" 
        class="helper-text"
      >
        <strong>There are no matching results.</strong>
        <br>
        <br>
        Improve your search results by:
        <br>
        <br>
        <ul>
          <li>Using different or fewer search terms.</li>
          <li>Checking your spelling.</li>
          <li>Removing or adjusting any filters.</li>
        </ul>
        Want to start over? Select “Clear all” to reset the search settings.
      </div>
    </div>
  </div>
  <!-- /Search Tabs -->
</template>

<script>
import _ from 'lodash';

export default {
  name: 'SearchTabs',
  props: {
    longformDocument: {
      type: Object,
      default: () => {
        return {};
      },
    },
    documentSection: {
      type: Object,
      default: () => {
        return {};
      },
    },
    searchThisPageDisabled: {
      type: Boolean,
      default: false,
    },
    postInfo: {
      type: Object,
      required: true,
    },
  },
  data: function() {
    return {
      searchCount: 0,
      searchQuery: null,
      searchedText: null,
      activeTab: 0,
    };
  },
  mounted () {
    let vm = this;
    vm.init();
  },
  beforeDestroy() {
    let vm = this;
    vm.$emit('clearAllPages');
    vm.clearPageSearch();
  },
  methods: {
    async init () { 
      let vm = this;
      if (vm.$route.name == 'SearchResult') {
        await vm.searchAllPages(vm.$route.query.term);
      }
      if (vm.$route.name == 'LongformDocument' && vm.$route.query.term) {
        await vm.searchPage(vm.$route.query.term);
      }
      if (vm.$route.name == 'Index') {
        vm.setAllPageSearchActive();
      }
      await vm.setTabbedIndex();
    },
    async setTabbedIndex() {
      let vm = this;
      let tab0 = this.$el.querySelectorAll(".tabs ul li")[0];
      let tab1 = this.$el.querySelectorAll(".tabs ul li")[1];
      tab0.tabIndex = 0;
      tab1.tabIndex = 0;
      tab0.addEventListener("keyup", function(event) {
        if (event.keyCode === 13) {
          event.preventDefault();
          vm.setPageSearchActive();
        }
      });
      tab1.addEventListener("keyup", function(event) {
        if (event.keyCode === 13) {
          event.preventDefault();
          vm.setAllPageSearchActive();
        }
      });
    },
    async searchAllPages(term = null) {
      let vm = this;
      vm.searchCount = 0;
      if (typeof term === 'string' && !vm.searchQuery) {
        vm.searchQuery = term;
      } else if (vm.searchQuery) {
        vm.$router.push({ name: 'SearchResult', query: { term: vm.searchQuery }}).catch(() => {});
      }
      if( vm.searchQuery ) {
        vm.searchedText = vm.searchQuery;
        vm.setAllPageSearchActive();
        let results = [];
        if(vm.longformDocument) {
          Object.entries(vm.longformDocument).forEach(( [ , section ] ) => {
            if(section.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
              section = vm.highlight(vm.searchQuery, section);
              results.push({
                title : section.section_title,
                description : section.section_copy,
                primary: section.section_number + ' ' + section.section_title,
                index: section.section_number,
                primaryIndex: section.section_number,
              });
            }
            Object.entries(section.children).forEach(( [ , section1 ] ) => {
              if(section1.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section1.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                section1 = vm.highlight(vm.searchQuery, section1);
                results.push({
                  title : section1.section_title,
                  description : section1.section_copy,
                  primary: section.section_number + ' ' + section.section_title,
                  index: section1.section_number,
                  primaryIndex: section.section_number,
                });
              }
              Object.entries(section1.children).forEach(( [ , section2 ] ) => {
                if(section2.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section2.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                  section2 = vm.highlight(vm.searchQuery, section2);
                  results.push({
                    title : section2.section_title,
                    description : section2.section_copy,
                    primary: section.section_number + ' ' + section.section_title,
                    index: section2.section_number,
                    primaryIndex: section.section_number,
                  });
                }
                Object.entries(section2.children).forEach(( [ , section3 ] ) => {
                  if(section3.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section3.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                    section3 = vm.highlight(vm.searchQuery, section3);
                    results.push({
                      title : section3.section_title,
                      description : section3.section_copy,
                      primary: section.section_number + ' ' + section.section_title,
                      index: section3.section_number,
                      primaryIndex: section.section_number,
                    });
                  }
                  Object.entries(section3.children).forEach(( [ , section4 ] ) => {
                    if(section4.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section4.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                      section4 = vm.highlight(vm.searchQuery, section4);
                      results.push({
                        title : section4.section_title,
                        description : section4.section_copy,
                        primary: section.section_number + ' ' + section.section_title,
                        index: section4.section_number,
                        primaryIndex: section.section_number,
                      });
                    }
                    Object.entries(section4.children).forEach(( [ , section5 ] ) => {
                      if(section5.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section5.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                        section5 = vm.highlight(vm.searchQuery, section5);
                        results.push({
                          title : section5.section_title,
                          description : section5.section_copy,
                          primary: section.section_number + ' ' + section.section_title,
                          index: section5.section_number,
                          primaryIndex: section.section_number,
                        });
                      }
                      Object.entries(section5.children).forEach(( [ , section6 ] ) => {
                        if(section6.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section6.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                          section6 = vm.highlight(vm.searchQuery, section6);
                          results.push({
                            title : section6.section_title,
                            description : section6.section_copy,
                            primary: section.section_number + ' ' + section.section_title,
                            index: section6.section_number,
                            primaryIndex: section.section_number,
                          });
                        }
                        Object.entries(section6.children).forEach(( [ , section7 ] ) => {
                          if(section7.section_title.toUpperCase().includes(vm.searchQuery.toUpperCase()) || section7.section_copy.toUpperCase().includes(vm.searchQuery.toUpperCase())) {
                            section7 = vm.highlight(vm.searchQuery, section7);
                            results.push({
                              title : section7.section_title,
                              description : section7.section_copy,
                              primary: section.section_number + ' ' + section.section_title,
                              index: section7.section_number,
                              primaryIndex: section.section_number,
                            });
                          }
                        });
                      });
                    });
                  });
                });
              });
            });
          });
        }

        results = _.groupBy(results, function(result) {
          return result.primary;
        });
        this.$emit('searchAllPages', results);
      } else {
        this.$emit('searchAllPages', null);
      }
    },
    setAllPageSearchActive() {
      this.activeTab = 1;
    },
    setPageSearchActive() {
      this.activeTab = 0;
    },
    clearPageSearch() {
      let vm = this;
      vm.searchQuery = null;
      vm.searchedText = vm.searchQuery;
      vm.searchPage();
      vm.$emit('searchThisPage', false);
    },
    clearAllPageSearch() {
      let vm = this;
      vm.searchQuery = null;
      vm.searchedText = vm.searchQuery;
      vm.searchCount = 0;
      vm.clearAllPages();
      this.$emit('searchAllPages', null);
    },
    searchPage(term = null) {
      let vm = this;
      vm.$emit('searchThisPage', true);
      if (typeof term === 'string' && !vm.searchQuery) {
        vm.searchQuery = term;
      }
      vm.searchedText = vm.searchQuery;
      vm.searchCount = 0;
      vm.documentSection = vm.highlight(vm.searchQuery, vm.documentSection);
      if(vm.documentSection.children) {
        Object.entries(vm.documentSection.children).forEach(( [ , section ] ) => {
          section = vm.highlight(vm.searchQuery, section);
          Object.entries(section.children).forEach(( [ , section1 ] ) => {
            section1 = vm.highlight(vm.searchQuery, section1);
            Object.entries(section1.children).forEach(( [ , section2 ] ) => {
              section2 = vm.highlight(vm.searchQuery, section2);
              Object.entries(section2.children).forEach(( [ , section3 ] ) => {
                section3 = vm.highlight(vm.searchQuery, section3);
                Object.entries(section3.children).forEach(( [ , section4 ] ) => {
                  section4 = vm.highlight(vm.searchQuery, section4);
                  Object.entries(section4.children).forEach(( [ , section5 ] ) => {
                    section5 = vm.highlight(vm.searchQuery, section5);
                    Object.entries(section5.children).forEach(( [ , section6 ] ) => {
                      section6 = vm.highlight(vm.searchQuery, section6);
                      Object.entries(section6.children).forEach(( [ , section7 ] ) => { // eslint-disable-line no-unused-vars
                        section7 = vm.highlight(vm.searchQuery, section7);
                      });
                    });
                  });
                });
              });
            });
          });
        });
      }
    },
    clearAllPages () {
      let vm = this;
      if(vm.longformDocument) {
        Object.entries(vm.longformDocument).forEach(( [ , section ] ) => {
          section = vm.clearHighlight( section );
          Object.entries(section.children).forEach(( [ , section1 ] ) => {
            section1 = vm.clearHighlight( section1 );
            Object.entries(section1.children).forEach(( [ , section2 ] ) => {
              section2 = vm.clearHighlight( section2 );
              Object.entries(section2.children).forEach(( [ , section3 ] ) => {
                section3 = vm.clearHighlight( section3 );
                Object.entries(section3.children).forEach(( [ , section4 ] ) => {
                  section4 = vm.clearHighlight( section4 );
                  Object.entries(section4.children).forEach(( [ , section5 ] ) => {
                    section5 = vm.clearHighlight( section5 );
                    Object.entries(section5.children).forEach(( [ , section6 ] ) => {
                      section6 = vm.clearHighlight( section6 );
                      Object.entries(section6.children).forEach(( [ , section7 ] ) => { // eslint-disable-line no-unused-vars
                        section7 = vm.clearHighlight( section7 );
                      });
                    });
                  });
                });
              });
            });
          });
        });
      }
    },
    clearHighlight(section) {
      let highlightRegex = new RegExp('<span class="highlightText" tabindex="0">', "gi");
      let spanRegex = new RegExp("</span>", "gi");
      if (highlightRegex.test(section.section_copy)) {
        section.section_copy = section.section_copy.replace(highlightRegex,'').replace(spanRegex, '');
      }
      if (highlightRegex.test(section.section_title)) {
        section.section_title = section.section_title.replace(highlightRegex,'').replace(spanRegex, '');
      }
      return section;
    },
    highlight(query, section) {
      let vm = this;
      section = vm.clearHighlight(section);
      if(query) {
        section.section_copy = section.section_copy.replace(new RegExp(query + "(?![^<>]*>)", "gi"), match => {
          vm.searchCount++;
          return '<span class="highlightText" tabindex="0">' + match + '</span>';
        });
        section.section_title = section.section_title.replace(new RegExp(query + "(?![^<>]*>)", "gi"), match => {
          vm.searchCount++;  
          return '<span class="highlightText" tabindex="0">' + match + '</span>';
        });
      } 
      return section;
    },
  },
};
</script>